import React from 'react';
import { X } from 'lucide-react';
import { format, parse } from 'date-fns';
import { tr } from 'date-fns/locale';
import StatusBadge from './StatusBadge';
import { CHANNEL_TR } from '../../i18n/tr';
import { maskPhoneNumber } from '../../utils/auth';
import { resolveDisplayStatus } from '../../utils/callStatus';

const DASH = '—';
const has = (v) => !(v === undefined || v === null || v === '');
const text = (v, fb = DASH) => (has(v) ? v : fb);

const formatDateTime = (iso) => {
  if (!iso) return DASH;
  try {
    return format(new Date(iso), 'd MMMM yyyy HH:mm:ss', { locale: tr });
  } catch {
    return iso;
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return DASH;
  try {
    return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy EEEE', { locale: tr });
  } catch {
    return dateStr;
  }
};

/**
 * Normalizes either a call (with nested .appointment) or a standalone appointment
 * into the standardized appointment-detail field set. Missing values fall back to
 * "—" (or "Bilinmiyor" for chassis) so the UI never breaks.
 */
export const normalizeDetail = (appointment, call) => {
  const a = appointment || call?.appointment || {};

  const status = call ? resolveDisplayStatus(call) : (
    a.confirmed || a.status === 'confirmed'
      ? 'Appointment Created'
      : a.status === 'pending'
        ? 'Appointment Requested'
        : 'Appointment Created'
  );

  const channel = a.channel || call?.channel;
  const kaynak = channel && CHANNEL_TR[channel] ? CHANNEL_TR[channel] : 'Telefon';
  const musteri = a.customerName || `${a.firstName || ''} ${a.lastName || ''}`.trim();

  return {
    status,
    kaynak,
    telefon: text(a.customerPhone || call?.phoneNumber),
    zaman: call?.callTime
      ? formatDateTime(call.callTime)
      : a.createdAt
        ? formatDateTime(a.createdAt)
        : DASH,
    sure: text(call?.duration),
    islem: text(a.visitType || a.serviceType || a.department || call?.serviceType),
    musteri: text(musteri),
    bolum: text(a.department),
    doktor: text(a.preferredDoctor),
    sigorta: text(a.insuranceType),
    sikayet: has(a.chiefComplaint) ? a.chiefComplaint : has(a.additionalComplaints) ? a.additionalComplaints : null,
    randevuTarihi: formatDateLong(a.date),
    randevuSaati: text(a.time),
    servis: text(a.serviceLocation),
    hat: has(a.line) ? a.line : null,
    talepEdilen: has(a.requestedTime) ? a.requestedTime : null,
    secilen: has(a.alternativeTime) ? a.alternativeTime : null,
    ekSikayet: has(a.additionalComplaints) ? a.additionalComplaints : null,
    hatirlatma: has(a.reminderNote)
      ? a.reminderNote
      : a.reminderScheduled
        ? 'Randevudan bir gün önce hatırlatma yapılacak'
        : null,
    not: has(a.afternoonNote) ? a.afternoonNote : null,
    ozet: call?.summary || a.summary || '',
    transkript: call?.transcript || a.transcript || '',
  };
};

const Field = ({ label, value }) => (
  <div className="flex flex-col">
    <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-100">{value}</dd>
  </div>
);

const AppointmentDetailModal = ({
  appointment = null,
  call = null,
  title = 'Randevu detayı',
  onClose,
  privacyMode = false,
}) => {
  const d = normalizeDetail(appointment, call);
  const telefon = privacyMode ? maskPhoneNumber(d.telefon) : d.telefon;

  const primaryRows = [
    ['Kaynak', d.kaynak],
    ['Telefon', telefon],
    ['Zaman', d.zaman],
    ['Süre', d.sure],
    ['Muayene Türü', d.islem],
    ['Hasta', d.musteri],
    ['Bölüm', d.bolum],
    ['Doktor', d.doktor],
    ['Sigorta', d.sigorta],
    ['Randevu Tarihi', d.randevuTarihi],
    ['Randevu Saati', d.randevuSaati],
  ];

  const hasExtras = d.talepEdilen || d.secilen || d.hat || d.ekSikayet || d.hatirlatma || d.not;

  return (
    <div className="glass-overlay fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="glass-modal max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-cockpit-900/80 p-4 backdrop-blur-xl">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div>
            <StatusBadge status={d.status} />
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            {primaryRows.map(([label, value]) => (
              <Field key={label} label={label} value={value} />
            ))}
          </dl>

          {hasExtras && (
            <div className="space-y-1.5 rounded-xl bg-white/[0.03] p-3 text-sm text-slate-300 ring-1 ring-white/10">
              {d.talepEdilen && (
                <p>
                  <span className="text-slate-500">Talep edilen ilk saat:</span> {d.talepEdilen}
                </p>
              )}
              {d.secilen && (
                <p>
                  <span className="text-slate-500">Seçilen alternatif saat:</span> {d.secilen}
                </p>
              )}
              {d.hat && (
                <p>
                  <span className="text-slate-500">Hat:</span> {d.hat}
                </p>
              )}
              {d.sikayet && (
                <p>
                  <span className="text-slate-500">Şikayet:</span> {d.sikayet}
                </p>
              )}
              {d.ekSikayet && (
                <p>
                  <span className="text-slate-500">İlave şikayet:</span> {d.ekSikayet}
                </p>
              )}
              {d.hatirlatma && (
                <p className="text-emerald-300/90">
                  <span className="text-slate-500">Hatırlatma:</span> {d.hatirlatma}
                </p>
              )}
              {d.not && (
                <p className="text-amber-200/90">
                  <span className="text-slate-500">Not:</span> {d.not}
                </p>
              )}
            </div>
          )}

          {!privacyMode && (
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Özet</p>
              <p className="text-sm leading-relaxed text-slate-200">{d.ozet || DASH}</p>
            </div>
          )}

          {!privacyMode && d.transkript && (
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Transkript</p>
              <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg bg-white/5 p-3 font-sans text-xs leading-relaxed text-slate-300 ring-1 ring-white/10">
                {d.transkript}
              </pre>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-white/10 p-4">
          <button type="button" onClick={onClose} className="btn-secondary">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailModal;
